import type { CvtSbrUploadFailureStatus, CvtStsUploadFailureStatus, VehicleGraphData } from '@packages/clients';

import { CvtUploadFailStatus } from '@packages/clients';

import type { CvtPresignedUrlsUploadResult } from './types';

import { BULLET_PREFIX_PATTERN, LINE_BREAK_PATTERN } from './constants';

/**
 *  Converts a string with bullet points (using either '*' or '-' or '•' as bullet characters) into an array of strings.
 *
 * @param text - The string containing bullet points.
 * @returns An array of strings, each representing a bullet point without the bullet character.
 */
export function bulletListToArray(text?: string): string[] {
    if (!text) {
        return [];
    }
    return splitIntoSentences(text).map(stripBulletPrefix);
}

/**
 * Fetches the SBR response data including graph data from the given URL. Returns an empty array if the fetch fails or if the data is not in the expected format.
 *
 * @param sbrResponseDataUrl - The URL to fetch the SBR response data from, which includes the graph data after processing a CVT file.
 * @returns A promise that resolves to the vehicle graph data included in the SBR response, or an empty array if the fetch fails or if the data is not in the expected format.
 */
export async function fetchSbrResponseGraphData(sbrResponseDataUrl: string): Promise<VehicleGraphData[]> {
    try {
        const response = await fetch(sbrResponseDataUrl);
        if (!response.ok) {
            return [];
        }
        const sbrData = await response.json();
        if (!sbrData?.data) {
            return [];
        }
        return sbrData.data;
    } catch {
        return [];
    }
}

/**
 * Uploads a file to both SBR and STS pre-signed URLs, and returns an object indicating which uploads failed, if any.
 *
 * @param file - the file to upload
 * @param sbrUploadUrl - the pre-signed URL to upload the file to for SBR
 * @param stsUploadUrl - the pre-signed URL to upload the file to for STS
 * @returns an object containing the upload failure status for both SBR and STS uploads
 * If an upload succeeded, the corresponding failure status will be an empty object.
 * If an upload failed, the corresponding failure status will contain the failure status and error message.
 */
export async function handlePresignedUrlsUpload(
    file: File,
    sbrUploadUrl: string,
    stsUploadUrl: string
): Promise<CvtPresignedUrlsUploadResult> {
    const [sbrResult, stsResult] = await Promise.allSettled([
        uploadFile(file, sbrUploadUrl),
        uploadFile(file, stsUploadUrl),
    ]);

    const sbrUploadFailureStatus: CvtSbrUploadFailureStatus =
        sbrResult.status === 'rejected'
            ? {
                  sbrFailStatus: CvtUploadFailStatus.SBR,
                  sbrFailMessage: sbrResult.reason?.message ?? 'Failed to upload file to SBR',
              }
            : {};

    const stsUploadFailureStatus: CvtStsUploadFailureStatus =
        stsResult.status === 'rejected'
            ? {
                  stsFailStatus: CvtUploadFailStatus.STS,
                  stsFailMessage: stsResult.reason?.message ?? 'Failed to upload file to STS',
              }
            : {};

    return { sbrUploadFailureStatus, stsUploadFailureStatus };
}

/**
 * Normalizes a message containing mixed `<br>` and newline separators into a
 * canonical string with `\n` as the only line separator. Empty and
 * whitespace-only lines are removed.
 *
 * @param text - The raw message, typically from a backend response.
 * @returns A normalized message, or an empty string if the input is empty.
 */
export function normalizeMessage(text?: string): string {
    return splitIntoSentences(text ?? '').join('\n');
}

/**
 * Splits a string into sentences based on line break delimiters (either HTML line break tags '<br>' or newline characters '\n'),
 * trims whitespace from each sentence, and filters out any empty sentences.
 *
 * @param text - The string to split into sentences.
 * @returns An array of sentences.
 */
function splitIntoSentences(text: string): string[] {
    return text
        .split(LINE_BREAK_PATTERN)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

/**
 * Removes bullet point characters ('*', '-', or '•') from the beginning of a string, along with any leading whitespace.
 *
 * @param line - A string that may start with a bullet point character ('*', '-', or '•') followed by whitespace.
 * @returns The input string with the bullet point character and any leading whitespace removed.
 */
function stripBulletPrefix(line: string): string {
    return line.replace(BULLET_PREFIX_PATTERN, '');
}

/**
 * Uploads a file to the given pre-signed URL using an HTTP PUT request.
 * Throws an error if the upload fails.
 *
 * @param file - The file to upload.
 * @param preSignedUploadUrl - The pre-signed URL to upload the file to.
 * @throws {Error} If the upload fails.
 */
async function uploadFile(file: File, preSignedUploadUrl: string): Promise<void> {
    const response = await fetch(preSignedUploadUrl, {
        method: 'PUT',
        body: file,
    });
    if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
    }
}
